import axios from 'axios';
import axiosRetry from 'axios-retry';
import { invalidateSession } from './sessionInvalidation';

export const client = axios.create({
  // baseURL: `${import.meta.env.VITE_API_URL ?? ''}/api` ?? '/api',
  baseURL: '/api',
  withCredentials: true,
  paramsSerializer: { indexes: null },
});

export type RefreshTokenResult =
  | { kind: 'success' }
  | { kind: 'auth-failure'; status: number }
  | { error: unknown; kind: 'network-failure' };

type RecordValue = Record<string, unknown>;

function asRecord(value: unknown): RecordValue | undefined {
  return typeof value === 'object' && value !== null ? (value as RecordValue) : undefined;
}

function getErrorStatus(error: unknown): number | undefined {
  const errorRecord = asRecord(error);
  const responseRecord = asRecord(errorRecord?.response);
  if (typeof responseRecord?.status === 'number') {
    return responseRecord.status;
  }
  return typeof errorRecord?.status === 'number' ? errorRecord.status : undefined;
}

function getErrorURL(error: unknown): string | undefined {
  const errorRecord = asRecord(error);
  const configRecord = asRecord(errorRecord?.config);
  return typeof configRecord?.url === 'string' ? configRecord.url : undefined;
}

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  const path = url.split('?')[0].replace(/\/+$/, '');
  return path.endsWith('/auth/refresh') || path.endsWith('/auth/logout');
}

export const refreshToken = async (): Promise<RefreshTokenResult> => {
  try {
    const response = await client.post('/auth/refresh', undefined, { timeout: 10_000 });
    if (response.status >= 400 && response.status < 500) {
      return { kind: 'auth-failure', status: response.status };
    }
    return { kind: 'success' };
  } catch (error: unknown) {
    const status = getErrorStatus(error);
    if (status !== undefined && status >= 400 && status < 500) {
      return { kind: 'auth-failure', status };
    }
    return { error, kind: 'network-failure' };
  }
};

let refreshPromise: Promise<RefreshTokenResult> | undefined;

export function getRefreshPromise(): Promise<RefreshTokenResult> {
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = undefined;
    });
  }
  return refreshPromise;
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = getErrorStatus(error);
    if (status === 401 && !isAuthEndpoint(getErrorURL(error))) {
      invalidateSession({ broadcast: true });
    }
    if (status === 429 && window.location.pathname !== '/rate-limit') {
      window.location.pathname = '/rate-limit';
      return;
    }
    if (status === 412 && window.location.pathname !== '/account-disabled') {
      window.location.pathname = '/account-disabled';
      return;
    }
    return Promise.reject(error);
  },
);

axiosRetry(client, {
  retries: 3,
  retryDelay: (retryCount) => {
    console.log(`Token expired. Retry attempt: ${retryCount}`);
    return retryCount * 500;
  },
  retryCondition: async (error) => {
    if (error.response?.status === 429 && window.location.pathname !== '/rate-limit') {
      window.location.pathname = '/rate-limit';
      return false;
    }
    if (error.response?.status === 412 && window.location.pathname !== '/account-disabled') {
      window.location.pathname = '/account-disabled';
      return false;
    }
    if (error.response?.status === 418 && !isAuthEndpoint(getErrorURL(error))) {
      const refreshResult = await getRefreshPromise();
      if (refreshResult.kind === 'success') {
        return true;
      }
      if (refreshResult.kind === 'auth-failure') {
        invalidateSession({ broadcast: true });
      }
      return false;
    }
    return false;
  },
});
